// THE THREE SEAMS THIS BATCH DOES NOT BUILD — interfaces, each with a fake, and nothing real behind them.
//
// Auth, App Check and payments are the plumbing a web client needs and does not have yet
// (WEB_PATHWAY_INVENTORY.md §7, "Client plumbing any real web client needs"). Naming them as
// interfaces now is what lets the screens be written once: the checkout screen calls `Checkout`, and
// whether that is a fake spending a credit or Stripe in test mode is a construction detail it never sees.
//
// NOTHING HERE TALKS TO A NETWORK. There is no Firebase config, no reCAPTCHA site key, no Stripe
// publishable key, and no project id anywhere in this package.

// ── identity ─────────────────────────────────────────────────────────────────────────────────────

// ONE ACCOUNT, SHARED WITH THE APP (ACC-2). The session, its port, its typed results and the order an account ends in
// live in `./auth.ts`; the fake pool and SDK in `./fakeAuth.ts`. Both sessions below are `createAuthSession`, so the fake
// cannot decide anything the real one does not. The prototype's email-plus-magic-link screen stays gone (§5b row 11).
export type {
  AccountEmails, AccountResult, ActionLink, ActionLinkResult, AppleRevocation, AuthBackend, AuthEnding, AuthRefusal,
  AuthSession, ChangePasswordResult, EndableAccount, LookAround, OpResult, SignInProvider, SignOutResult,
} from "./auth";
export { endAccountSession, parseActionLink, UNBUILT_ACCOUNT_EMAILS } from "./auth";
// ACC-1: the REAL account emails, over the transport (v2Credits' four ops, contract 2.16.0). ACC-3a passes it to
// `createFirebaseAuthSession` when it wires the screens; until then the real session keeps `UNBUILT_ACCOUNT_EMAILS`.
export { accountEmailResult, createTransportAccountEmails } from "./accountEmails";

import { createAuthSession, UNBUILT_ACCOUNT_EMAILS, type AccountEmails, type AuthBackend, type AuthEnding, type AuthSession } from "./auth";
import { createFakeAccountEmails, createFakeAuthBackend, createFakeAuthWorld, type FakeAuthWorld } from "./fakeAuth";

/** The REAL session, over the Firebase seam (`services/firebase.ts`): the backend the SDK started and the token reader
 *  it already guards. `start` has already waited for the restored sign-in, so `signIn` finds someone and does nothing.
 *  The account emails are the server's (ACC-1); until they exist the session says `emailsUnavailable`. */
export function createFirebaseAuthSession(
  backend: AuthBackend,
  tokens: { getIdToken(): Promise<string> },
  emails: AccountEmails = UNBUILT_ACCOUNT_EMAILS,
): AuthSession {
  return createAuthSession({ backend, emails, getIdToken: () => tokens.getIdToken() });
}

export interface FakeAuthSessionOptions {
  /** The pool and the browser it runs in. Pass the same world to a second session to model a reload. */
  world?: FakeAuthWorld;
  /** Make the fake Firebase refuse a delete, a sign-out, or both — the arms `endAccountSession` handles. */
  refuse?: { deleteUser?: boolean; signOut?: boolean };
}

/** The fake: the same session over the fake pool, with our server's emails faked the way ACC-1 will build them. */
export function createFakeAuthSession(
  options: FakeAuthSessionOptions = {},
): AuthSession & { readonly ended: AuthEnding | null; readonly world: FakeAuthWorld } {
  const world = options.world ?? createFakeAuthWorld();
  if (options.refuse !== undefined) world.refuse = { ...options.refuse };
  const backend = createFakeAuthBackend(world);
  const session = createAuthSession({
    backend,
    emails: createFakeAccountEmails(world, () => backend.getIdToken()),
    now: () => world.clock,
  });
  let ended: AuthEnding | null = null;
  // The session's own getters stay live through the prototype; `Object.assign` would freeze them into values.
  return Object.defineProperties(Object.create(session) as AuthSession & { readonly ended: AuthEnding | null; readonly world: FakeAuthWorld }, {
    ended: { get: () => ended },
    world: { value: world },
    endAfterDeletion: {
      value: async (options?: { readonly signInDeleted?: boolean }): Promise<AuthEnding> => {
        ended = await session.endAfterDeletion(options);
        return ended;
      },
    },
  });
}

// ── App Check ────────────────────────────────────────────────────────────────────────────────────

/**
 * App Check on web is reCAPTCHA Enterprise. The server's `appCheckPasses` verifies any App Check token
 * for the project (`functions/src/auth.ts`), so a web token passes the existing check unchanged
 * (W2_DESIGN.md, finding 5) — but which app ids v2 allowlists, and the CORS origins, are **D14, and
 * Tyler's**. Nothing in this batch registers an app or names a project.
 */
export interface AppCheckTokens {
  getToken(): Promise<string>;
}

export function createFakeAppCheckTokens(token = "fake-app-check-token"): AppCheckTokens {
  return { async getToken() { return token; } };
}

// ── payments ─────────────────────────────────────────────────────────────────────────────────────

/**
 * WHERE THE OWNER GOES TO PAY: STRIPE'S HOSTED CHECKOUT (WEB-PAY-1; R199), in test mode unless the
 * server's held live-payments switch is on (LAUNCH-PAY). Which mode is the server's to say; this seam is the same in both.
 *
 * The SERVER decides everything about a purchase — what is for sale, at what price, whether the returning-owner price
 * applies, where Stripe sends the owner back — and `v2CreateCheckout` answers one URL. This seam does the one thing a
 * browser must do with it: leave for it. Credits arrive by Stripe's webhook, never through this seam, so the page the
 * owner returns to reads the balance from `v2Credits` `get` like every other screen.
 *
 * NO PRICE, NO KEY AND NO STRIPE SDK IS IN THIS PACKAGE. Stripe's hosted page needs none of them on our side.
 */
export type CheckoutRedirect =
  /** The browser is leaving for Stripe; nothing more happens on this page. */
  | { kind: "leaving" }
  /** The fake stood in for Stripe and the webhook: the owner "paid", and the client goes where Stripe would send it. */
  | { kind: "simulated"; returnTo: "/checkout/return" };

export interface Checkout {
  /** Send the owner to the URL the server answered. Refuses any URL that is not Stripe's hosted checkout. */
  redirectToStripe(url: string): Promise<CheckoutRedirect>;
}

/** The only host this client will ever send an owner to for a payment. The server checks the same thing. */
export const STRIPE_HOSTED_CHECKOUT = "https://checkout.stripe.com/";

/** The real one: this tab navigates to Stripe, and only to Stripe. `window.open(url, "_self")` rather than an
 *  assignment to the location: this package never touches the page's location (`test/stagingSeams.test.ts`, "NO
 *  FALLBACK ORIGIN EXISTS"), and leaving the page is all this needs. */
export function createBrowserCheckout(assign: (url: string) => void = (url) => { window.open(url, "_self", "noopener"); }): Checkout {
  return {
    async redirectToStripe(url) {
      if (!url.startsWith(STRIPE_HOSTED_CHECKOUT)) {
        throw new Error("refusing to send the owner to a payment page that is not Stripe's hosted checkout");
      }
      assign(url);
      return { kind: "leaving" };
    },
  };
}

/**
 * THE FAKE STANDS IN FOR STRIPE AND ITS WEBHOOK TOGETHER: `onPaid` is the fake server completing the purchase it was
 * asked for (`completeFakeCheckout` in transport/fake), exactly as the real webhook would, and the owner is sent where
 * Stripe's success URL would send them. It still refuses a URL that is not Stripe's, so a screen that built its own
 * redirect fails here as it would for real.
 */
export function createFakeCheckout(onPaid: () => void): Checkout {
  return {
    async redirectToStripe(url) {
      if (!url.startsWith(STRIPE_HOSTED_CHECKOUT)) {
        throw new Error("refusing to send the owner to a payment page that is not Stripe's hosted checkout");
      }
      onPaid();
      return { kind: "simulated", returnTo: "/checkout/return" };
    },
  };
}
